/**
 * Life4Earth V2 — Project Milestones API Adapter
 *
 * Endpoints:
 *   • FP path: /api/projectmilestones?projectId={guid}
 *   • MP path: /api/projectmilestones/mini?miniProjectGuid={guid}
 *
 * Task #226 made the underlying project_milestones table polymorphic
 * (project_id XOR mini_project_id, with a content_type discriminator).
 * The single controller serves both surfaces; this client mirrors that
 * shape with parallel FP / MP fetch+create helpers and a shared update /
 * delete pair that work by milestone id regardless of owner.
 *
 * MP mutations dispatch EVT_MINI_PROJECT_MUTATED so portfolio consumers
 * (useApiMiniProjects, useApiMiniProjectPortfolioSignals) refetch — same
 * pattern wired for MP status edits in Task #221.
 */

import { apiFetch } from './client';
import { dispatchMiniProjectMutated } from './miniProjects';

// ── API Response Type ─────────────────────────────────────

export interface ProjectMilestoneResponse {
  id: string;
  /** Task #226 — polymorphic discriminator. "project" | "mini_project". */
  contentType: 'project' | 'mini_project';
  /** Set when contentType="project"; null otherwise. */
  projectId: string | null;
  /** Set when contentType="mini_project"; null otherwise. */
  miniProjectGuid: string | null;
  name: string;
  targetDate: string | null;
  actualDate: string | null;
  sortOrder: number;
  source: string;
  ganttTaskId: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Request Types ──────────────────────────────────────────

export interface CreateMilestoneRequest {
  name: string;
  targetDate?: string | null;
  actualDate?: string | null;
  sortOrder?: number;
}

export interface UpdateMilestoneRequest {
  name?: string;
  targetDate?: string | null;
  actualDate?: string | null;
  sortOrder?: number;
  clearActualDate?: boolean;
}

// ── FP API Functions ─────────────────────────────────────

/** List all standalone milestones for a Full Project. */
export async function fetchProjectMilestones(
  projectGuid: string,
): Promise<ProjectMilestoneResponse[]> {
  return apiFetch<ProjectMilestoneResponse[]>(
    `/api/projectmilestones?projectId=${encodeURIComponent(projectGuid)}`,
  );
}

/** Create a milestone for a Full Project. */
export async function createProjectMilestone(
  projectGuid: string,
  req: CreateMilestoneRequest,
): Promise<ProjectMilestoneResponse> {
  return apiFetch<ProjectMilestoneResponse>(
    `/api/projectmilestones?projectId=${encodeURIComponent(projectGuid)}`,
    { method: 'POST', body: JSON.stringify(req) },
  );
}

// ── MP API Functions (Task #227) ─────────────────────────

/** List all milestones for a Mini-Project. */
export async function fetchMiniProjectMilestones(
  miniProjectGuid: string,
): Promise<ProjectMilestoneResponse[]> {
  return apiFetch<ProjectMilestoneResponse[]>(
    `/api/projectmilestones/mini?miniProjectGuid=${encodeURIComponent(miniProjectGuid)}`,
  );
}

/** Create a milestone for a Mini-Project. Dispatches
 *  EVT_MINI_PROJECT_MUTATED on success so the portfolio aggregator
 *  picks up the change. */
export async function createMiniProjectMilestone(
  miniProjectGuid: string,
  req: CreateMilestoneRequest,
): Promise<ProjectMilestoneResponse> {
  const result = await apiFetch<ProjectMilestoneResponse>(
    `/api/projectmilestones/mini?miniProjectGuid=${encodeURIComponent(miniProjectGuid)}`,
    { method: 'POST', body: JSON.stringify(req) },
  );
  dispatchMiniProjectMutated(miniProjectGuid);
  return result;
}

// ── Owner-agnostic mutations ────────────────────────────

/** Update a milestone by id. Works for both FP and MP milestones —
 *  ownership is determined by the milestone row itself, not the
 *  request body. */
export async function updateProjectMilestone(
  milestoneId: string,
  req: UpdateMilestoneRequest,
): Promise<ProjectMilestoneResponse> {
  const result = await apiFetch<ProjectMilestoneResponse>(
    `/api/projectmilestones/${milestoneId}`,
    { method: 'PUT', body: JSON.stringify(req) },
  );
  // Broadcast for MP-owned milestones so the portfolio refreshes.
  // FP rows naturally no-op (miniProjectGuid is null).
  if (result?.contentType === 'mini_project' && result.miniProjectGuid) {
    dispatchMiniProjectMutated(result.miniProjectGuid);
  }
  return result;
}

/** Delete a milestone by id. Works for both FP and MP milestones. */
export async function deleteProjectMilestone(
  milestoneId: string,
): Promise<void> {
  // No ownership info comes back from a 204 NoContent. Callers that
  // need to broadcast (the MP table widget) can do so themselves with
  // the miniProjectGuid they already have in scope.
  return apiFetch<void>(
    `/api/projectmilestones/${milestoneId}`,
    { method: 'DELETE' },
  );
}
